import { api } from "../api";
import type {
  ClassItem,
  ClassListItem,
  ClassCreate,
  ClassUpdate,
  ClassSession,
  SessionCreate,
  SessionUpdate,
  SessionParticipant,
  SessionParticipantBatchItem,
  SessionParticipantOverlapCheckPayload,
  SessionParticipantOverlapItem,
  SessionParticipantsSyncPayload,
  SessionWithDetails,
  ClassEnrollment,
  ClassEnrollCreate,
  ClassEnrollmentUpdate,
  StudentBatchEnrollPayload,
  StudentBatchEnrollResponse,
  ClassEnrollmentTransferPayload,
  ClassEnrollmentTransferResponse,
  ClassAuditEvent,
  LeaveRequestResponse,
  LeaveRequestStatus,
  PaginatedResponse,
} from "../../types/domain";

export type MonthlyAttendanceMember = {
  user_id: string | null;
  full_name: string;
  email: string;
  dob: string | null;
  role?: string;
};

export type MonthlyAttendanceSession = {
  session_id: string;
  date: string;
  start_time: string;
  attendance: boolean[];
};

export type MonthlyAttendanceGrid = {
  members: MonthlyAttendanceMember[];
  sessions: MonthlyAttendanceSession[];
  totals: number[];
};

export type MonthlyAttendanceResponse = {
  year: number;
  month: number;
  students: MonthlyAttendanceGrid;
  staff: MonthlyAttendanceGrid;
};

export const classMgmtService = {
  getAll: (params?: { skip?: number; limit?: number; center_id?: string }) => {
    const query = new URLSearchParams();
    if (params?.skip !== undefined) query.set("skip", String(params.skip));
    if (params?.limit !== undefined) query.set("limit", String(params.limit));
    if (params?.center_id) query.set("center_id", params.center_id);
    return api.get<PaginatedResponse<ClassListItem>>(`/classes?${query}`);
  },
  getById: (id: string) => api.get<ClassItem>(`/classes/${id}`),
  getAuditEvents: (classId: string, params?: { session_id?: string; skip?: number; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.session_id) query.set("session_id", params.session_id);
    if (params?.skip !== undefined) query.set("skip", String(params.skip));
    if (params?.limit !== undefined) query.set("limit", String(params.limit));
    return api.get<PaginatedResponse<ClassAuditEvent>>(`/classes/${classId}/audit-events?${query}`);
  },
  create: (data: ClassCreate) => api.post<ClassItem>("/classes", data),
  update: (id: string, data: ClassUpdate) => api.patch<ClassItem>(`/classes/${id}`, data),

  // Sessions
  getSessions: (classId: string) =>
    api.get<ClassSession[]>(`/classes/${classId}/sessions`),
  getSessionById: (sessionId: string) =>
    api.get<ClassSession>(`/sessions/${sessionId}`),
  getSessionsFeed: (params?: {
    date_from?: string;
    date_to?: string;
    class_id?: string;
    status?: string;
    skip?: number;
    limit?: number;
  }) => {
    const query = new URLSearchParams();
    if (params?.date_from) query.set("date_from", params.date_from);
    if (params?.date_to) query.set("date_to", params.date_to);
    if (params?.class_id) query.set("class_id", params.class_id);
    if (params?.status) query.set("status", params.status);
    if (params?.skip !== undefined) query.set("skip", String(params.skip));
    if (params?.limit !== undefined) query.set("limit", String(params.limit));
    return api.get<PaginatedResponse<SessionWithDetails>>(`/sessions/feed?${query}`);
  },
  getStaffTimesheetSessions: (params: {
    date_from: string;
    date_to: string;
    role?: "teacher" | "trainer";
    center_id?: string;
  }) => {
    const query = new URLSearchParams();
    query.set("date_from", params.date_from);
    query.set("date_to", params.date_to);
    if (params.role) query.set("role", params.role);
    if (params.center_id) query.set("center_id", params.center_id);
    return api.get<PaginatedResponse<SessionWithDetails>>(`/sessions/staff-timesheet?${query}`);
  },
  createSession: (data: SessionCreate) =>
    api.post<ClassSession>("/sessions", data),
  updateSession: (sessionId: string, data: SessionUpdate) =>
    api.patch<ClassSession>(`/sessions/${sessionId}`, data),
  getSessionParticipants: (sessionId: string) =>
    api.get<SessionParticipant[]>(`/sessions/${sessionId}/participants`),
  getSessionParticipantsBatch: (sessionIds: string[]) => {
    if (sessionIds.length === 0) {
      return Promise.resolve([] as SessionParticipantBatchItem[]);
    }
    const query = new URLSearchParams();
    sessionIds.forEach((sessionId) => query.append("session_ids", sessionId));
    return api.get<SessionParticipantBatchItem[]>(
      `/sessions/participants/batch?${query}`
    );
  },
  checkSessionParticipantOverlaps: (data: SessionParticipantOverlapCheckPayload) =>
    api.post<SessionParticipantOverlapItem[]>("/sessions/participant-overlaps", data),
  syncSessionParticipants: (sessionId: string, data: SessionParticipantsSyncPayload) =>
    api.put<SessionParticipant[]>(`/sessions/${sessionId}/participants`, data),
  // Additive — adds one student without touching the rest of the roster.
  // Prefer this over syncSessionParticipants when the caller only knows
  // about the single student it's adding (syncSessionParticipants replaces
  // the full roster and would otherwise drop teachers/trainers it doesn't
  // know about).
  addSessionStudent: (sessionId: string, studentId: string) =>
    api.post<SessionParticipant>(
      `/sessions/${sessionId}/participants/students/${studentId}`
    ),
  deleteSession: (sessionId: string) =>
    api.delete<void>(`/sessions/${sessionId}`),

  // Enrollments
  getEnrollments: (classId: string) =>
    api.get<ClassEnrollment[]>(`/classes/${classId}/enrollments`),
  enroll: (classId: string, data: ClassEnrollCreate) =>
    api.post<ClassEnrollment>(`/classes/${classId}/enrollments`, data),
  // Enrolls into several classes and adds to several sessions in one call.
  // Best-effort per item — check the response for which items succeeded.
  batchEnroll: (studentId: string, data: StudentBatchEnrollPayload) =>
    api.post<StudentBatchEnrollResponse>(
      `/classes/enroll-batch/${studentId}`,
      data
    ),
  updateEnrollment: (classId: string, enrollmentId: string, data: ClassEnrollmentUpdate) =>
    api.patch<ClassEnrollment>(`/classes/${classId}/enrollments/${enrollmentId}`, data),
  transferEnrollment: (classId: string, enrollmentId: string, data: ClassEnrollmentTransferPayload) =>
    api.post<ClassEnrollmentTransferResponse>(`/classes/${classId}/enrollments/${enrollmentId}/transfer`, data),
  removeEnrollment: (classId: string, enrollmentId: string) =>
    api.delete<void>(`/classes/${classId}/enrollments/${enrollmentId}`),

  // Monthly attendance
  getMonthlyAttendance: (classId: string, year: number, month: number) =>
    api.get<MonthlyAttendanceResponse>(
      `/classes/${classId}/monthly-attendance?year=${year}&month=${month}`
    ),

  // Leave requests ("xin nghỉ phép") — reviewed by class managers (QLL).
  listLeaveRequests: (
    classId: string,
    params?: { status?: LeaveRequestStatus; skip?: number; limit?: number }
  ) => {
    const query = new URLSearchParams();
    if (params?.status) query.set("status", params.status);
    if (params?.skip !== undefined) query.set("skip", String(params.skip));
    if (params?.limit !== undefined) query.set("limit", String(params.limit));
    return api.get<PaginatedResponse<LeaveRequestResponse>>(
      `/classes/${classId}/leave-requests?${query}`
    );
  },
  reviewLeaveRequest: (
    requestId: string,
    status: "PENDING" | "APPROVED" | "REJECTED"
  ) =>
    api.patch<LeaveRequestResponse>(`/leave-requests/${requestId}`, {
      status,
    }),

  exportMonthlyAttendance: async (classId: string, year: number, month: number) => {
    const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";
    const token = sessionStorage.getItem("access_token");
    const res = await fetch(
      `${BASE_URL}/classes/${classId}/export-monthly?year=${year}&month=${month}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new Error(body || `Export failed: ${res.status}`);
    }
    return res.blob();
  },
};
